import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Rocket, Loader2, CheckCircle2 } from "lucide-react";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/register", { ...form, email: form.email.trim().toLowerCase() });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <Rocket className="h-6 w-6 text-blue-500" />
          <span className="font-heading font-bold text-lg">ROKET</span>
        </div>
        {done ? (
          <div data-testid="register-success" className="border border-emerald-500/40 bg-emerald-500/10 rounded-sm p-5 text-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-3" />
            <p className="text-sm text-emerald-300 font-medium">Registration received</p>
            <p className="text-xs text-zinc-400 mt-2">An administrator must approve your account before you can sign in.</p>
            <Link to="/login" className="inline-block mt-4 text-blue-400 text-xs hover:underline">Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="register-form">
            <h2 className="font-heading text-2xl font-semibold tracking-tight">Request access</h2>
            <p className="text-sm text-zinc-500 mt-1 mb-6">Operations accounts require admin approval.</p>
            {error && <div data-testid="register-error" className="mb-4 px-3 py-2 rounded-sm border border-red-500/40 bg-red-500/10 text-red-400 text-xs font-mono">{error}</div>}

            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Full name</label>
            <input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full mb-4 bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono focus:border-blue-500 outline-none" />
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Email</label>
            <input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full mb-4 bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono focus:border-blue-500 outline-none" />
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Password</label>
            <input data-testid="register-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full mb-6 bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono focus:border-blue-500 outline-none" />
            <button data-testid="register-submit" disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm font-medium py-2.5 rounded-sm flex items-center justify-center gap-2 transition-colors duration-150">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Request access
            </button>
            <p className="mt-6 text-xs text-zinc-500 text-center">
              Already approved? <Link to="/login" className="text-blue-400 hover:underline">Sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
